import { useState, FormEvent } from 'react';

const projectTypes = [
  'Carpet Installation',
  'Hardwood Flooring',
  'Laminate Flooring',
  'Vinyl Flooring',
  'Marine & Specialty Carpet',
  'Floor Replacement & Repairs',
  'Commercial Project',
  'Other',
];

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function FinalCTA() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const msg = data.get('message') as string;
    if (msg && msg.length > 500) return;
    setStatus('sending');
    const params = new URLSearchParams();
    data.forEach((val, key) => params.append(key, val as string));
    try {
      const res = await fetch('https://readdy.ai/api/form/d71clieuimmp87kg1et0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); setCharCount(0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <>
      {/* CTA Banner */}
      <section className="relative py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5d6ad18bfc786e81894606207541fe55.jpg"
            alt="Premium flooring transformation"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <span className="section-tag mb-5 block">Take the Next Step</span>
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-6">
            Upgrade Your Floors<br />with Confidence
          </h2>
          <p className="text-white/65 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Expert guidance, premium materials, and flawless installation — backed by years of trusted service across New Jersey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary text-base"
            >
              Request a Design Consultation
            </button>
            <a href="tel:1-866-732-2966" className="btn-secondary text-base flex items-center justify-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill"></i>
              </span>
              Call 1-866-732-2966
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Info */}
            <div>
              <span className="section-tag mb-4 block">Get In Touch</span>
              <h2 className="section-heading mb-6">Start Your Flooring Project Today</h2>
              <p className="text-warm-gray text-base leading-relaxed mb-10">
                Fill out the form and a flooring specialist will reach out — typically within minutes during business hours.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-map-pin-fill', label: 'Location', content: '8 Tuscan Dr, Freehold, NJ 07728', href: 'https://maps.google.com/?q=8+Tuscan+Dr,+Freehold,+NJ+07728' },
                  { icon: 'ri-phone-fill', label: 'Toll Free', content: '1-866-732-2966', href: 'tel:1-866-732-2966' },
                  { icon: 'ri-phone-line', label: 'Local', content: '732-577-1496', href: 'tel:732-577-1496' },
                  { icon: 'ri-mail-fill', label: 'Email', content: 'sales@valuecarpetinc.com', href: 'mailto:sales@valuecarpetinc.com' },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 items-start">
                    <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-charcoal">
                      <i className={`${item.icon} text-gold text-base`}></i>
                    </span>
                    <div>
                      <p className="text-charcoal font-semibold text-sm mb-0.5">{item.label}</p>
                      <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="nofollow noreferrer" className="text-warm-gray text-sm hover:text-gold transition-colors">
                        {item.content}
                      </a>
                    </div>
                  </div>
                ))}
                <div className="flex gap-4 items-start">
                  <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-charcoal">
                    <i className="ri-time-fill text-gold text-base"></i>
                  </span>
                  <div>
                    <p className="text-charcoal font-semibold text-sm mb-0.5">Response Time</p>
                    <p className="text-warm-gray text-sm">We respond within minutes during business hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg p-8 lg:p-10" style={{ boxShadow: '0 8px 48px rgba(44,44,44,0.08)' }}>
              {status === 'success' ? (
                <div className="text-center py-12">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-cream mx-auto mb-5">
                    <i className="ri-checkbox-circle-fill text-gold text-3xl"></i>
                  </span>
                  <h3 className="font-serif text-2xl text-charcoal mb-3">Request Received!</h3>
                  <p className="text-warm-gray text-sm leading-relaxed">
                    A flooring specialist will be in touch shortly — typically within minutes.
                  </p>
                  <button onClick={() => setStatus('idle')} className="btn-primary mt-8">
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-5">
                  <div className="mb-2">
                    <h3 className="font-serif text-2xl text-charcoal">Request a Design Consultation</h3>
                    <p className="text-warm-gray text-sm mt-1">Free consultation — no obligation</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-charcoal text-xs font-semibold uppercase tracking-wide mb-2">Full Name <span className="text-gold">*</span></label>
                      <input type="text" name="name" required placeholder="Your full name" className="w-full border border-cream-dark bg-cream rounded-md px-4 py-3 text-sm text-charcoal placeholder-warm-gray/50 focus:outline-none focus:border-gold transition-colors" />
                    </div>
                    <div>
                      <label className="block text-charcoal text-xs font-semibold uppercase tracking-wide mb-2">Phone <span className="text-gold">*</span></label>
                      <input type="tel" name="phone" required placeholder="Your phone number" className="w-full border border-cream-dark bg-cream rounded-md px-4 py-3 text-sm text-charcoal placeholder-warm-gray/50 focus:outline-none focus:border-gold transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-charcoal text-xs font-semibold uppercase tracking-wide mb-2">Email <span className="text-gold">*</span></label>
                    <input type="email" name="email" required placeholder="your@email.com" className="w-full border border-cream-dark bg-cream rounded-md px-4 py-3 text-sm text-charcoal placeholder-warm-gray/50 focus:outline-none focus:border-gold transition-colors" />
                  </div>
                  <div>
                    <label className="block text-charcoal text-xs font-semibold uppercase tracking-wide mb-2">Project Type</label>
                    <select name="project_type" defaultValue="" className="w-full border border-cream-dark bg-cream rounded-md px-4 py-3 text-sm text-charcoal focus:outline-none focus:border-gold transition-colors cursor-pointer">
                      <option value="" disabled>Select a service...</option>
                      {projectTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-charcoal text-xs font-semibold uppercase tracking-wide mb-2">Message</label>
                    <textarea name="message" rows={4} maxLength={500} placeholder="Tell us about your space, size, timeline, or any questions..." className="w-full border border-cream-dark bg-cream rounded-md px-4 py-3 text-sm text-charcoal placeholder-warm-gray/50 focus:outline-none focus:border-gold transition-colors resize-none" onChange={(e) => setCharCount(e.target.value.length)} />
                    <p className="text-warm-gray/50 text-xs text-right mt-1">{charCount}/500</p>
                  </div>
                  {status === 'error' && (
                    <p className="text-red-500 text-sm text-center">Something went wrong. Please call us directly.</p>
                  )}
                  <button type="submit" disabled={status === 'sending'} className="btn-primary text-base justify-center flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {status === 'sending' ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                    ) : (
                      <>Request Consultation <i className="ri-arrow-right-line"></i></>
                    )}
                  </button>
                  <p className="text-warm-gray/50 text-xs text-center">
                    <i className="ri-lock-2-line text-gold/60 mr-1"></i>
                    Your information is secure and never shared.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
